module.exports = function (MouseDragHandler, PlayheadLayer, PointsLayer, SegmentsLayer, WaveformAxis, WaveformShape, Utils, Konva) {
    'use strict';
    function WaveformZoomView(waveformData, container, peaks) {
        var self = this;
        self._originalWaveformData = waveformData;
        self._container = container;
        self._peaks = peaks;
        self._onTimeUpdate = self._onTimeUpdate.bind(self);
        self._onPlay = self._onPlay.bind(self);
        self._onPause = self._onPause.bind(self);
        self._onWindowResize = self._onWindowResize.bind(self);
        self._onKeyboardLeft = self._onKeyboardLeft.bind(self);
        self._onKeyboardRight = self._onKeyboardRight.bind(self);
        self._onKeyboardShiftLeft = self._onKeyboardShiftLeft.bind(self);
        self._onKeyboardShiftRight = self._onKeyboardShiftRight.bind(self);
        self._peaks.on('player.timeupdate', self._onTimeUpdate);
        self._peaks.on('player.play', self._onPlay);
        self._peaks.on('player.pause', self._onPause);
        self._peaks.on('window_resize', self._onWindowResize);
        self._peaks.on('keyboard.left', self._onKeyboardLeft);
        self._peaks.on('keyboard.right', self._onKeyboardRight);
        self._peaks.on('keyboard.shift_left', self._onKeyboardShiftLeft);
        self._peaks.on('keyboard.shift_right', self._onKeyboardShiftRight);
        self._enableAutoScroll = true;
        self._amplitudeScale = 1;
        self._timeLabelPrecision = peaks.options.timeLabelPrecision;
        self._options = peaks.options;
        self._data = null;
        self._pixelLength = 0;
        var initialZoomLevel = self._options.zoomLevels[peaks.zoom.getZoom()];
        self._zoomLevelAuto = false;
        self._zoomLevelSeconds = null;
        self._resizeTimeoutId = null;
        self._resampleData({ scale: initialZoomLevel });
        self._width = container.clientWidth;
        self._height = container.clientHeight || self._options.height;
        self._frameOffset = 0;
        self._stage = new Konva.Stage({
            container: container,
            width: self._width,
            height: self._height
        });
        self._waveformLayer = new Konva.Layer({ listening: false });
        self._createWaveform();
        self._segmentsLayer = new SegmentsLayer(peaks, self, true);
        self._segmentsLayer.addToStage(self._stage);
        self._pointsLayer = new PointsLayer(peaks, self, true);
        self._pointsLayer.addToStage(self._stage);
        self._createAxisLabels();
        self._playheadLayer = new PlayheadLayer({
            player: self._peaks.player,
            view: self,
            showPlayheadTime: self._options.showPlayheadTime,
            playheadColor: self._options.playheadColor,
            playheadTextColor: self._options.playheadTextColor,
            playheadFontFamily: self._options.fontFamily,
            playheadFontSize: self._options.fontSize,
            playheadFontStyle: self._options.fontStyle
        });
        self._playheadLayer.addToStage(self._stage);
        var time = self._peaks.player.getCurrentTime();
        self._syncPlayhead(time);
        self._mouseDragHandler = new MouseDragHandler(self._stage, {
            onMouseDown: function (mousePosX) {
                this.initialFrameOffset = self._frameOffset;
                this.mouseDownX = mousePosX;
            },
            onMouseMove: function (mousePosX) {
                var diff = this.mouseDownX - mousePosX;
                var newFrameOffset = Utils.clamp(this.initialFrameOffset + diff, 0, self._pixelLength - self._width);
                if (newFrameOffset !== this.initialFrameOffset) {
                    self._updateWaveform(newFrameOffset);
                }
            },
            onMouseUp: function () {
                if (!self._mouseDragHandler.isDragging()) {
                    var mouseDownX = Math.floor(this.mouseDownX);
                    var pixelIndex = self._frameOffset + mouseDownX;
                    var time = self.pixelsToTime(pixelIndex);
                    var duration = self._getDuration();
                    if (time > duration) {
                        time = duration;
                    }
                    self._playheadLayer.updatePlayheadTime(time);
                    self._peaks.player.seek(time);
                }
            }
        });
        this._stage.on('dblclick', function (event) {
            var mousePosX = event.evt.layerX;
            var pixelIndex = self._frameOffset + mousePosX;
            var time = self.pixelsToTime(pixelIndex);
            self._peaks.emit('zoomview.dblclick', time);
        });
    }
    WaveformZoomView.prototype.getName = function () {
        return 'zoomview';
    };
    WaveformZoomView.prototype._onTimeUpdate = function (time) {
        if (this._mouseDragHandler.isDragging()) {
            return;
        }
        this._syncPlayhead(time);
    };
    WaveformZoomView.prototype._onPlay = function (time) {
        this._playheadLayer.updatePlayheadTime(time);
    };
    WaveformZoomView.prototype._onPause = function (time) {
        this._playheadLayer.stop(time);
    };
    WaveformZoomView.prototype._onWindowResize = function () {
        var self = this;
        var width = self._container.clientWidth;
        if (!self._zoomLevelAuto) {
            self._width = width;
            self._stage.width(width);
            self._updateWaveform(self._frameOffset);
        } else {
            if (self._resizeTimeoutId) {
                clearTimeout(self._resizeTimeoutId);
                self._resizeTimeoutId = null;
            }
            if (width !== 0) {
                self._width = width;
                self._stage.width(width);
                self._resizeTimeoutId = setTimeout(function () {
                    self._width = width;
                    self._data = self._originalWaveformData.resample(width);
                    self._stage.width(width);
                    self._updateWaveform(self._frameOffset);
                }, 500);
            }
        }
    };
    WaveformZoomView.prototype._onKeyboardLeft = function () {
        this._keyboardScroll(-1, false);
    };
    WaveformZoomView.prototype._onKeyboardRight = function () {
        this._keyboardScroll(1, false);
    };
    WaveformZoomView.prototype._onKeyboardShiftLeft = function () {
        this._keyboardScroll(-1, true);
    };
    WaveformZoomView.prototype._onKeyboardShiftRight = function () {
        this._keyboardScroll(1, true);
    };
    WaveformZoomView.prototype._keyboardScroll = function (direction, large) {
        var increment;
        if (large) {
            increment = direction * this._width;
        } else {
            increment = direction * this.timeToPixels(this._options.nudgeIncrement);
        }
        this._updateWaveform(this._frameOffset + increment);
    };
    WaveformZoomView.prototype.setWaveformData = function (waveformData) {
        this._originalWaveformData = waveformData;
    };
    WaveformZoomView.prototype._syncPlayhead = function (time) {
        this._playheadLayer.updatePlayheadTime(time);
        if (this._enableAutoScroll) {
            var pixelIndex = this.timeToPixels(time);
            var endThreshold = this._frameOffset + this._width - 100;
            if (pixelIndex >= endThreshold || pixelIndex < this._frameOffset) {
                this._frameOffset = pixelIndex - 100;
                if (this._frameOffset < 0) {
                    this._frameOffset = 0;
                }
                this._updateWaveform(this._frameOffset);
            }
        }
    };
    WaveformZoomView.prototype._getScale = function (duration) {
        return duration * this._data.sample_rate / this._width;
    };
    function isAutoScale(options) {
        return Utils.objectHasProperty(options, 'scale') && options.scale === 'auto' || Utils.objectHasProperty(options, 'seconds') && options.seconds === 'auto';
    }
    WaveformZoomView.prototype.setZoom = function (options) {
        var scale;
        if (isAutoScale(options)) {
            var seconds = this._peaks.player.getDuration();
            if (!Utils.isValidTime(seconds)) {
                return false;
            }
            this._zoomLevelAuto = true;
            this._zoomLevelSeconds = null;
            scale = this._getScale(seconds);
        } else {
            if (Utils.objectHasProperty(options, 'scale')) {
                this._zoomLevelSeconds = null;
                scale = options.scale;
            } else if (Utils.objectHasProperty(options, 'seconds')) {
                if (!Utils.isValidTime(options.seconds)) {
                    return false;
                }
                this._zoomLevelSeconds = options.seconds;
                scale = this._getScale(options.seconds);
            }
            this._zoomLevelAuto = false;
        }
        if (scale < this._originalWaveformData.scale) {
            this._peaks.logger('peaks.zoomview.setZoom(): zoom level must be at least ' + this._originalWaveformData.scale);
            scale = this._originalWaveformData.scale;
        }
        var currentTime = this._peaks.player.getCurrentTime();
        var apexTime;
        var playheadOffsetPixels = this._playheadLayer.getPlayheadOffset();
        if (playheadOffsetPixels >= 0 && playheadOffsetPixels < this._width) {
            apexTime = currentTime;
        } else {
            playheadOffsetPixels = this._width / 2;
            apexTime = this.pixelsToTime(this._frameOffset + playheadOffsetPixels);
        }
        var prevScale = this._scale;
        this._resampleData({ scale: scale });
        var apexPixel = this.timeToPixels(apexTime);
        this._frameOffset = apexPixel - playheadOffsetPixels;
        this._updateWaveform(this._frameOffset);
        this._playheadLayer.zoomLevelChanged();
        this._playheadLayer.updatePlayheadTime(currentTime);
        this._peaks.emit('zoom.update', scale, prevScale);
        return true;
    };
    WaveformZoomView.prototype._resampleData = function (options) {
        this._data = this._originalWaveformData.resample(options);
        this._scale = this._data.scale;
        this._pixelLength = this._data.length;
    };
    WaveformZoomView.prototype.getStartTime = function () {
        return this.pixelsToTime(this._frameOffset);
    };
    WaveformZoomView.prototype.getEndTime = function () {
        return this.pixelsToTime(this._frameOffset + this._width);
    };
    WaveformZoomView.prototype.setStartTime = function (time) {
        if (time < 0) {
            time = 0;
        }
        if (this._zoomLevelAuto) {
            time = 0;
        }
        this._updateWaveform(this.timeToPixels(time));
    };
    WaveformZoomView.prototype.timeToPixels = function (time) {
        return Math.floor(time * this._data.sample_rate / this._data.scale);
    };
    WaveformZoomView.prototype.pixelsToTime = function (pixels) {
        return pixels * this._data.scale / this._data.sample_rate;
    };
    WaveformZoomView.prototype.getFrameOffset = function () {
        return this._frameOffset;
    };
    WaveformZoomView.prototype.getWidth = function () {
        return this._width;
    };
    WaveformZoomView.prototype.getHeight = function () {
        return this._height;
    };
    WaveformZoomView.prototype._getDuration = function () {
        return this._peaks.player.getDuration();
    };
    WaveformZoomView.prototype.setAmplitudeScale = function (scale) {
        if (!Utils.isNumber(scale) || !Number.isFinite(scale)) {
            throw new Error('view.setAmplitudeScale(): Scale must be a valid number');
        }
        this._amplitudeScale = scale;
        this._waveformLayer.draw();
        this._segmentsLayer.draw();
    };
    WaveformZoomView.prototype.getAmplitudeScale = function () {
        return this._amplitudeScale;
    };
    WaveformZoomView.prototype.getWaveformData = function () {
        return this._data;
    };
    WaveformZoomView.prototype._createWaveform = function () {
        this._waveformShape = new WaveformShape({
            color: this._options.zoomWaveformColor,
            view: this
        });
        this._waveformLayer.add(this._waveformShape);
        this._stage.add(this._waveformLayer);
        this._peaks.emit('zoomview.displaying', 0, this.pixelsToTime(this._width));
    };
    WaveformZoomView.prototype._createAxisLabels = function () {
        this._axisLayer = new Konva.Layer({ listening: false });
        this._axis = new WaveformAxis(this, {
            axisGridlineColor: this._options.axisGridlineColor,
            axisLabelColor: this._options.axisLabelColor,
            axisLabelFontFamily: this._options.fontFamily,
            axisLabelFontSize: this._options.fontSize,
            axisLabelFontStyle: this._options.fontStyle
        });
        this._axis.addToLayer(this._axisLayer);
        this._stage.add(this._axisLayer);
    };
    WaveformZoomView.prototype._updateWaveform = function (frameOffset) {
        var upperLimit;
        if (this._pixelLength < this._width) {
            frameOffset = 0;
            upperLimit = this._width;
        } else {
            upperLimit = this._pixelLength - this._width;
        }
        frameOffset = Utils.clamp(frameOffset, 0, upperLimit);
        this._frameOffset = frameOffset;
        var playheadPixel = this._playheadLayer.getPlayheadPixel();
        this._playheadLayer.updatePlayheadTime(this.pixelsToTime(playheadPixel));
        this._waveformLayer.draw();
        this._axisLayer.draw();
        var frameStartTime = this.pixelsToTime(this._frameOffset);
        var frameEndTime = this.pixelsToTime(this._frameOffset + this._width);
        this._pointsLayer.updatePoints(frameStartTime, frameEndTime);
        this._segmentsLayer.updateSegments(frameStartTime, frameEndTime);
        this._peaks.emit('zoomview.displaying', frameStartTime, frameEndTime);
    };
    WaveformZoomView.prototype.setWaveformColor = function (color) {
        this._waveformShape.setWaveformColor(color);
        this._waveformLayer.draw();
    };
    WaveformZoomView.prototype.showPlayheadTime = function (show) {
        this._playheadLayer.showPlayheadTime(show);
    };
    WaveformZoomView.prototype.setTimeLabelPrecision = function (precision) {
        this._timeLabelPrecision = precision;
        this._playheadLayer.updatePlayheadText();
    };
    WaveformZoomView.prototype.formatTime = function (time) {
        return Utils.formatTime(time, this._timeLabelPrecision);
    };
    WaveformZoomView.prototype.enableAutoScroll = function (enable) {
        this._enableAutoScroll = enable;
    };
    WaveformZoomView.prototype.enableMarkerEditing = function (enable) {
        this._segmentsLayer.enableEditing(enable);
        this._pointsLayer.enableEditing(enable);
    };
    WaveformZoomView.prototype.fitToContainer = function () {
        if (this._container.clientWidth === 0 && this._container.clientHeight === 0) {
            return;
        }
        var updateWaveform = false;
        if (this._container.clientWidth !== this._width) {
            this._width = this._container.clientWidth;
            this._stage.width(this._width);
            var resample = false;
            var resampleOptions;
            if (this._zoomLevelAuto) {
                resample = true;
                resampleOptions = { width: this._width };
            } else if (this._zoomLevelSeconds !== null) {
                resample = true;
                resampleOptions = { scale: this._getScale(this._zoomLevelSeconds) };
            }
            if (resample) {
                try {
                    this._resampleData(resampleOptions);
                    updateWaveform = true;
                } catch (error) {
                }
            }
        }
        this._height = this._container.clientHeight;
        this._stage.height(this._height);
        this._waveformShape.fitToView();
        this._playheadLayer.fitToView();
        this._segmentsLayer.fitToView();
        this._pointsLayer.fitToView();
        if (updateWaveform) {
            this._updateWaveform(this._frameOffset);
        }
        this._stage.draw();
    };
    WaveformZoomView.prototype.destroy = function () {
        if (this._resizeTimeoutId) {
            clearTimeout(this._resizeTimeoutId);
            this._resizeTimeoutId = null;
        }
        this._peaks.off('player.timeupdate', this._onTimeUpdate);
        this._peaks.off('player.play', this._onPlay);
        this._peaks.off('player.pause', this._onPause);
        this._peaks.off('window_resize', this._onWindowResize);
        this._peaks.off('keyboard.left', this._onKeyboardLeft);
        this._peaks.off('keyboard.right', this._onKeyboardRight);
        this._peaks.off('keyboard.shift_left', this._onKeyboardShiftLeft);
        this._peaks.off('keyboard.shift_right', this._onKeyboardShiftRight);
        this._playheadLayer.destroy();
        this._segmentsLayer.destroy();
        this._pointsLayer.destroy();
        if (this._stage) {
            this._stage.destroy();
            this._stage = null;
        }
    };
    return WaveformZoomView;
}(require('./mouse-drag-handler'), require('./playhead-layer'), require('./points-layer'), require('./segments-layer'), require('./waveform-axis'), require('./waveform-shape'), require('./utils'), require('konva'));